import { PeriodType } from '@/common/Graph/types';
import { Dayjs } from 'dayjs';
import { chunk } from 'lodash';
import { API } from '../Client';
import {
  AlpacaCryptoAggBar,
  AlpacaCryptoBatchQuote,
  AlpacaCryptoQuote,
  AssetInfo,
  HoodAnalystRatingsWithSummaries,
  HoodStockEarning,
  IEXBatchQuoteResponse,
  IEXCompanyInfo,
  IEXQuote,
  PolygonAggBar,
  PolygonDailyPrice,
  PolygonPrevMarketDayPrice,
  RelatedAsset,
  YHFEtfHolding,
  YHFQuote,
  FMPOutlook,
  BatchQuoteDto,
  QuoteDto,
  DynamicBatchQuoteDto,
} from './types';
import moment from 'moment';
import MarketService from '../Market';
import { PolygonQuote } from '@/hooks/usePositions';

namespace AssetService {
  /**
   * Fetches basic info about the stock from our db
   *
   * @param symbol stock ticker, doesn't work for crypto yet
   * @returns basic info about the stock
   */
  export async function getAssetInfo(symbol: string): Promise<AssetInfo> {
    const res = await API.get(`/assets-info/${symbol}`);
    return res?.data;
  }

  /**
   * Get top ETF holdings of given stock from Yahoo Finance
   *
   * @param symbol stock ticker
   * @returns top etf holdings
   */
  export async function getEtfHoldings(symbol: string): Promise<YHFEtfHolding[]> {
    const res = await API.get(`/assets-info/${symbol}/etf-holdings`);
    return res?.data || [];
  }

  /**
   * Fetches analyst ratings
   *
   * @param symbol
   * @param limit
   * @returns analyst ratings
   */
  export async function getAnalystRatings(
    symbol: string,
    limit: number = 3
  ): Promise<HoodAnalystRatingsWithSummaries> {
    const res = await API.get(`/assets-info/${symbol}/analyst-ratings-with-summaries`, {
      params: {
        limit,
      },
    });
    return res?.data;
  }

  /**
   * Fetches earnings
   *
   * @param symbol
   * @returns earnings
   */
  export async function getEarnings(symbol: string): Promise<HoodStockEarning[]> {
    const res = await API.get(`/assets-info/${symbol}/earnings`);
    return res?.data?.slice(-5);
  }

  /**
   * Fetches description
   *
   * @param symbol
   * @returns description
   */
  export async function getDescription(symbol: string): Promise<string> {
    const res = await API.get(`/assets-info/${symbol}/description`);
    return res?.data;
  }

  /**
   * Fetches related stocks
   *
   * @param symbol
   * @returns related stocks
   */
  export async function getRelatedAssets(
    symbol: string,
    includeMetadata = false,
    includeLatestQuote = false
  ): Promise<RelatedAsset[]> {
    const res = await API.get(`/assets-info/${symbol}/related`, {
      params: {
        includeMetadata,
        includeLatestQuote,
      },
    });
    return res?.data || [];
  }

  /**
   * Fetches company information
   *
   * @param symbol
   * @returns company information
   */
  export async function getCompanyInfo(symbol: string): Promise<IEXCompanyInfo> {
    const res = await API.get(`/assets-info/${symbol}/company-info`);
    return res?.data;
  }

  /**
   * Fetches stock overview
   *
   * @param symbol
   * @returns stock overview (contains a ton of data)
   */
  export async function getStockOutlook(symbol: string): Promise<FMPOutlook> {
    const res = await API.get(`/assets-info/${symbol}/outlook`);
    return res?.data;
  }

  /**
   * Fetches latest quote from IEX
   *
   * @param symbol
   * @param isCrypto
   * @returns latest quote
   */
  export async function getQuote(
    symbol: string,
    isCrypto: boolean = false
  ): Promise<AlpacaCryptoQuote | IEXQuote> {
    const res = await API.get(
      `/assets-info/pricing/${symbol}/${isCrypto ? 'alpaca-crypto-quote' : 'iex-quote'}`
    );
    return res?.data;
  }

  /**
   * Fetches multiple latest quotes from IEX
   *
   * @param symbols
   * @param isCrypto
   * @returns multiple latest quotes
   */
  export async function getBatchQuote(
    symbols: string[],
    isCrypto = false
  ): Promise<{
    [symbol: string]: DynamicBatchQuoteDto<typeof isCrypto> & {
      realLatestPrice: number;
      realLatestTimestamp: Date;
    };
  }> {
    const chunks = chunk(symbols, 700);

    const promises = chunks.map((symbolChunks) =>
      API.get(`/assets-info/pricing/${isCrypto ? 'polygon-crypto-quote' : 'polygon-quote'}/batch`, {
        params: {
          symbols: symbolChunks,
        },
      })
    );
    const res = await Promise.all(promises);
    const finalResult: { [symbol: string]: DynamicBatchQuoteDto<typeof isCrypto> } = res?.reduce(
      (acc, curr) => ({ ...acc, ...(curr?.data || {}) }),
      {}
    );

    const response: {
      [symbol: string]: DynamicBatchQuoteDto<typeof isCrypto> & {
        realLatestPrice: any;
        realLatestTimestamp: any;
      };
    } = {};

    const marketOpen = (await MarketService.isMarketOpen()) || isCrypto;
    const isHoliday = await MarketService.isHoliday();
    for (let i = 0; i < Object.keys(finalResult).length; i++) {
      const key = Object.keys(finalResult)[i];
      const quote: DynamicBatchQuoteDto<typeof isCrypto> = finalResult[key];

      const latestPrice = quote?.latestPrice;

      let previousClose;

      if (isCrypto) {
        previousClose = latestPrice - (quote?.change || 0);
      } else {
        previousClose = isHoliday
          ? latestPrice - (quote?.change || 0)
          : (quote as QuoteDto)?.previousClose;
      }

      const change = previousClose && !isHoliday ? latestPrice - previousClose : quote?.change;
      const changePercent =
        previousClose && !isHoliday
          ? (latestPrice - previousClose) / previousClose
          : quote?.changePercent / 100;

      response[key] = {
        ...quote,
        realLatestPrice: latestPrice,
        realLatestTimestamp: marketOpen ? moment().valueOf().toString() : quote?.latestUpdate,
        previousClose,
        change,
        changePercent,
      };
    }

    return response;
  }

  /**
   * Fetches multiple latest quotes from Yahoo Finance
   *
   * @param symbols
   * @returns multiple latest quotes from Yahoo Finances
   */
  export async function getYHFBatchQuote(symbols: string[]): Promise<YHFQuote[]> {
    const res = await API.get('/assets-info/pricing/yhf-quote/batch', {
      params: {
        symbols,
      },
    });

    return res?.data || [];
  }

  /**
   * Fetches the aggregate bars based on given stock symbol and time range
   *
   * @param symbol ticker symbol for stock
   * @param range PeriodRange enum to get data for
   * @returns aggregate bars
   */
  export async function getHistoricalBarsByRange(
    symbol: string,
    range: PeriodType,
    isCrypto: boolean = false,
    limit?: number
  ): Promise<PolygonAggBar[] | AlpacaCryptoAggBar[]> {
    const res = await API.get(`/assets-info/pricing/${symbol}/historical-bars-by-range`, {
      params: {
        range,
        isCrypto,
        limit,
      },
    });

    return res?.data || [];
  }

  /**
   * Fetches the aggregate bars based on given stock symbol and the specified date
   *
   * @param symbol ticker symbol for stock
   * @param date Date to get the data for
   * @returns aggregate bars
   */
  export async function getHistoricalBarsByDate(
    symbol: string,
    date: string,
    isCrypto: boolean = false
  ): Promise<PolygonAggBar[] | AlpacaCryptoAggBar[]> {
    const res = await API.get(`/assets-info/pricing/${symbol}/historical-bars-by-date`, {
      params: {
        date,
        isCrypto,
      },
    });

    return res?.data || [];
  }

  /**
   * Fetches the aggregate bars for the current day (has 15 min delay)
   *
   * @param symbol ticker symbol for stock
   * @returns aggregate bars
   */
  export async function getIntradayBars(
    symbol: string,
    isCrypto: boolean = false
  ): Promise<PolygonAggBar[] | AlpacaCryptoAggBar[]> {
    const res = await API.get(`/assets-info/pricing/${symbol}/intraday-bars`, {
      params: {
        isCrypto,
      },
    });

    return res?.data || [];
  }

  /**
   * Fetches open price, close price, etc. of the specified date
   *
   * @param symbol ticker symbol for stock
   * @param date Date to get the data for
   * @returns prices for the day
   */
  export async function getPricesByDate(symbol: string, date: Dayjs): Promise<PolygonDailyPrice> {
    const res = await API.get(`/assets-info/pricing/${symbol}/prices-by-date`, {
      params: {
        date: date.format(),
      },
    });

    return res?.data;
  }

  /**
   * Fetches open price, close price, etc. of the previous market day
   *
   * @param symbol ticker symbol for stock
   * @returns previous market day prices
   */
  export async function getPreviousMarketDayPrices(
    symbol: string
  ): Promise<PolygonPrevMarketDayPrice> {
    const res = await API.get(`/assets-info/pricing/${symbol}/prev-market-day-prices`);

    return res?.data;
  }

  /**
   * Fetches close price of the previous market day of the last market day (only applies to the 1 Day Graph)
   *
   * @param symbol symbol for stock, crypto is not allowed here
   * @returns close price of the previous market day of the last market day
   */
  export async function getPreviousClosePriceBeforeLastMarketDay(symbol: string): Promise<number> {
    const res = await API.get(
      `/assets-info/pricing/${symbol}/prev-close-price-before-last-market-day`
    );

    return res?.data;
  }
}

export default AssetService;
